/**
 * useMcpOAuth — React hook that encapsulates the full MCP OAuth 2.1
 * re-authorization flow for chat interruptions.
 *
 * Designed to be used in ChatContainer via useChatOrchestrator.
 * The hook does not own any state of its own, it uses shared chat state
 * provided by the parent component.
 */

import { api } from "../utils/api";
import { retryChat } from "../api/QuestionsApi";
import { fetch_stream } from "../utils/streaming";

export const useMcpOAuth = ({
  streamingAvailable,
  iniMessage,
  t,
  // Shared chat state setters from the parent component
  setMessages,
  setIsLoading,
  setQuestion,
  setChatId,
  handleExecutionStatus,
  // Error reporting
  setStreamingError,
  setStatusModal,
  showStatusModal,
}) => {
  /**
   * Retry the last user message after an OAuth popup.
   * Supports both streaming and non-streaming modes.
   * @param {string} chatId
   * @param {boolean|undefined} success
   */
  const retryChatAfterOAuth = (chatId, success = undefined) => {
    setIsLoading(true);
    const streamBody = success !== undefined ? { success } : {};

    if (streamingAvailable) {
      let localStreamEnded = false;
      const lastline_regex = /\n\[END OF MESSAGE\]\n/;

      fetch_stream(
        `cognitive/chats_stream/${chatId}/retry/`,
        streamBody,
        (chunk) => {
          if (chunk.includes("[END OF MESSAGE]")) {
            setMessages((prevMessages) => [
              ...prevMessages.slice(0, -1),
              {
                content: prevMessages[prevMessages.length - 1]?.loading
                  ? chunk
                      .replace(lastline_regex, "")
                      .replace(lastline_regex, "")
                  : `${prevMessages[prevMessages.length - 1]?.content || ""}${chunk.replace(lastline_regex, "")}`,
                is_user: false,
                loading: false,
              },
            ]);

            const contentAfterEnd = chunk.split(lastline_regex)[1] || "";
            if (contentAfterEnd !== "") {
              try {
                const jsonData = JSON.parse(contentAfterEnd);
                setQuestion(jsonData);
                setChatId(jsonData.id);
                handleExecutionStatus(jsonData.execution_status);
                if (iniMessage) {
                  setMessages(jsonData.messages.slice(1));
                } else {
                  setMessages(jsonData.messages);
                }
                setIsLoading(false);
              } catch (e) {
                console.log(e);
              }
            }

            localStreamEnded = true;
          } else {
            if (localStreamEnded) {
              const jsonData = JSON.parse(chunk);
              setQuestion(jsonData);
              setChatId(jsonData.id);
              handleExecutionStatus(jsonData.execution_status);
              if (iniMessage) {
                setMessages(jsonData.messages.slice(1));
              } else {
                setMessages(jsonData.messages);
              }
              setIsLoading(false);
            } else {
              setMessages((prevMessages) => [
                ...prevMessages.slice(0, -1),
                {
                  content: prevMessages[prevMessages.length - 1]?.loading
                    ? chunk
                    : `${prevMessages[prevMessages.length - 1]?.content || ""}${chunk}`,
                  is_user: false,
                  loading: false,
                },
              ]);
            }
          }
        },
        () => {},
        (error) => {
          if (error?.type === "chatRunning") {
            setStreamingError(error);
            setStatusModal(true);
          }
        }
      );
    } else {
      retryChat(chatId, success)
        .then((response) => {
          const data = response.data;
          setQuestion(data);
          setChatId(data.id);
          handleExecutionStatus(data.execution_status);
          const msgs = data.messages || [];
          if (iniMessage) {
            setMessages(msgs.slice(1));
          } else {
            setMessages(msgs);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Retry error:", error);
          showStatusModal(null, error?.customMessage);
          setIsLoading(false);
        });
    }
  };

  /**
   * Open the OAuth popup for the WAITING_FOR_OAUTH flow.
   * Calls retry/ with { success: true/false } -- the backend decides to keep the
   * needs auth state or go back to normal chatting.
   */
  const openWaitingForAuthPopup = (serverId, chatId) => {
    const popup = window.open("", "mcp-oauth", "width=600,height=700");
    if (!popup) {
      retryChatAfterOAuth(chatId, false);
      return;
    }

    api
      .get(`cognitive/mcp-servers/${serverId}/oauth/authorize/`)
      .then(({ data }) => {
        if (!data?.authorization_url) {
          popup.close();
          retryChatAfterOAuth(chatId, false);
          return;
        }

        popup.location.href = data.authorization_url;

        const onMessage = (event) => {
          if (event.data?.type !== "mcp-oauth-callback") return;
          window.removeEventListener("message", onMessage);
          setMessages((prev) => [
            ...prev.slice(0, -1),
            { content: t("thinking"), role: "assistant", loading: true },
          ]);
          retryChatAfterOAuth(chatId, event.data.success === true);
        };
        window.addEventListener("message", onMessage);
      })
      .catch(() => {
        popup.close();
        retryChatAfterOAuth(chatId, false);
      });
  };

  return { openWaitingForAuthPopup, retryChatAfterOAuth };
};
